import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { toast } from '~/utils/toast';
import Button from '~/components/Button';
import Modal from '~/components/Modal';
import { api, type RouterInputs } from '~/utils/api';
import { serverError } from '~/utils/serverError';
import { isValidLinkedinCompanyUrl, isValidUrlRegex } from '~/utils/validator';

import { PlusIcon } from '@heroicons/react/24/outline';
import { Permission, ScannerAssociation, SocialMediaType } from '@prisma/client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { AppListingType } from '~/config';
import usePrevious from '~/hooks/usePrevious';
import useAbility from '~/hooks/useAbility';
import ActionSettings from './ActionSettings';
import JobSettings from './JobSettings';
import { fromScheduler } from './ScheduledJobsTable';
import ScheduledJobTabs from './ScheduledJobTabs';
import { sanitizeUsername } from './utils';

type UpdateScannerJobInput = RouterInputs['scanner']['updateScannerJob'];
type CreateScannerJobInput = RouterInputs['scanner']['createScannerJob'];

export enum Frequency {
  ONCE = 'ONCE',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

const TABS = ['Details', 'Actions'] as const;

const toScheduler = (
  frequency: Frequency,
  weekDay: number,
  monthDay: number,
): string | undefined => {
  switch (frequency) {
    case Frequency.DAILY: {
      return '0 7 * * *';
    }
    case Frequency.WEEKLY: {
      return `0 7 * * ${weekDay}`;
    }
    case Frequency.MONTHLY: {
      return `0 7 ${monthDay} * *`;
    }
  }
};

const validateFields = ['name', 'documentEvaluationTypeId', 'username', 'urls'] as const;

export type Errors = Partial<Record<(typeof validateFields)[number], string>>;

type NewJobButtonProps = {
  isEdit?: boolean;
  modalProperties: {
    openModal: () => void;
    modalRef: RefObject<HTMLDialogElement | null>;
    closeModal: () => void;
    isOpen: boolean;
  };
  initialInput: Partial<UpdateScannerJobInput>;
  isInfluencer: boolean;
  hideButton?: boolean;
  onSuccess?: (action: 'created' | 'updated') => void;
};

const getAppStoreAppId = (url: string) => {
  if (url.startsWith('https://apps.apple.com/')) {
    return new URL(url).pathname.split('/').pop()?.replace('id', '');
  }
  return;
};

const getGooglePlayAppId = (url: string) => {
  if (url.startsWith('https://play.google.com/')) {
    return new URL(url).searchParams.get('id') || undefined;
  }
  return;
};

export default function NewJobButton({
  isEdit,
  modalProperties,
  initialInput,
  isInfluencer,
  hideButton,
  onSuccess: onSuccessCallback,
}: NewJobButtonProps) {
  const { openModal, closeModal, modalRef, isOpen } = modalProperties;
  const { mutate: createScannerJob, isLoading: isCreateScannerJobLoading } =
    api.scanner.createScannerJob.useMutation();
  const { mutate: updateScannerJob, isLoading: isUpdateScannerJobLoading } =
    api.scanner.updateScannerJob.useMutation();

  const isLoading = isUpdateScannerJobLoading || isCreateScannerJobLoading;

  const [input, setInput] = useState<Partial<UpdateScannerJobInput>>(initialInput);

  const [frequency, setFrequency] = useState<Frequency>(Frequency.WEEKLY);
  const [weekDay, setWeekDay] = useState<number>(0);
  const [monthDay, setMonthDay] = useState<number>(1);
  const [activeTabIdx, setActiveTabIdx] = useState<number>(0);
  const [errors, setErrors] = useState<Errors>({});
  const tabIdPrefix = useId();

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const prevIsOpen = usePrevious(isOpen);
  const ability = useAbility();
  const canEditJob = ability?.can('do', Permission.EDIT_SCANNER_JOB);

  useEffect(() => {
    // Do this instead of Modal's `onClose` prop as it can be interfered by other modals
    if (isOpen && prevIsOpen !== isOpen) {
      setInput(initialInput);

      const {
        frequency: initialFrequency,
        weekDay: initialWeekDay,
        monthDay: initialMonthDay,
      } = fromScheduler(initialInput.scheduler ?? null);
      setFrequency(initialFrequency);
      setWeekDay(initialWeekDay);
      setMonthDay(initialMonthDay);
      setActiveTabIdx(0);
      setErrors({});
    }
  }, [isOpen, initialInput]);

  const utils = api.useUtils();

  const modalBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    modalBodyRef.current?.scrollTo({ top: 0 });
  }, [activeTabIdx]);

  const isSocialMedia = !!input.socialMedia?.type;

  const validateInput = () => {
    const errs: Errors = {};
    if (!input.name) {
      errs.name = 'Please enter a name';
    }

    if (!input.documentEvaluationTypeId) {
      errs.documentEvaluationTypeId = 'Please select a document evaluation type';
    }

    if (
      input.socialMedia &&
      !input.socialMedia.username &&
      input.socialMedia.type !== SocialMediaType.FACEBOOK &&
      input.socialMedia.type !== SocialMediaType.LINKEDIN
    ) {
      errs.username = 'Please enter an account username';
    }

    if (
      input.appListing ||
      input.socialMedia?.type === SocialMediaType.LINKEDIN ||
      input.socialMedia?.type === SocialMediaType.FACEBOOK ||
      !input.socialMedia
    ) {
      let validUrls = !!input.urls?.length;

      if (validUrls) {
        validUrls = !!input.urls?.every((url) => {
          if (input.socialMedia?.type === SocialMediaType.LINKEDIN) {
            return isValidLinkedinCompanyUrl(url);
          } else if (input.appListing?.type === AppListingType.APP_STORE) {
            return !!getAppStoreAppId(url);
          } else if (input.appListing?.type === AppListingType.GOOGLE_PLAY) {
            return !!getGooglePlayAppId(url);
          } else {
            return isValidUrlRegex(url);
          }
        });
      }

      if (!validUrls) {
        errs.urls = 'Please enter a valid URL';
      }
    }

    setErrors(errs);
    return errs;
  };

  const handleSubmit = () => {
    const errs = validateInput();

    if (Object.keys(errs).some((key) => !!errs[key as keyof Errors])) {
      console.error('Form errors', errs);
      setActiveTabIdx(0);
      toast.error('Please fix the errors in the form.');
      return;
    }

    let username = input.socialMedia?.username;

    if (username) {
      username = sanitizeUsername(username, input.socialMedia?.type!);

      if (!username) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          username: `Please enter a valid account username for ${input.socialMedia?.type}`,
        }));
        console.error('Invalid username');
        toast.error('Please fix the username field in the form.');
        return;
      }
    }

    const upsertScannerJobInput: CreateScannerJobInput = {
      ...input,
      name: input.name || '',
      documentEvaluationTypeId: input.documentEvaluationTypeId!,
      scheduler: toScheduler(frequency, weekDay, monthDay),
      association: input.association || ScannerAssociation.OWN,
      archiveMode: input.archiveMode,
      ...(isSocialMedia && input.socialMedia
        ? {
            socialMedia: {
              ...input.socialMedia,
              username,
            },
          }
        : {}),
    };

    // convert url to app ID for Google Play or App Store scan
    if (upsertScannerJobInput.appListing) {
      let appId: string | undefined = '';
      const url = upsertScannerJobInput.urls?.[0];
      if (url) {
        if (upsertScannerJobInput.appListing?.type === AppListingType.GOOGLE_PLAY) {
          appId = getGooglePlayAppId(url);
        } else if (upsertScannerJobInput.appListing?.type === AppListingType.APP_STORE) {
          appId = getAppStoreAppId(url);
        }
      }
      if (!appId) {
        errors.urls = 'Please enter a valid store link';
        return;
      }
      upsertScannerJobInput.appListing.appId = appId;
    }

    const onSuccess = (action: 'created' | 'updated') => () => {
      toast.success(`Scheduled job ${action} successfully.`);
      closeModal();
      utils.scanner.getAllScannerJobs.invalidate();
      utils.scanner.getScannerJobByBrand.invalidate();
      onSuccessCallback?.(action);
    };
    const getOptions = (action: 'created' | 'updated') => ({
      onSuccess: onSuccess(action),
      onError: serverError,
    });
    if (isEdit) {
      updateScannerJob(
        {
          id: initialInput.id!,
          ...upsertScannerJobInput,
        },
        getOptions('updated'),
      );
    } else {
      createScannerJob(upsertScannerJobInput, getOptions('created'));
    }
  };

  return (
    <>
      {!isEdit && !hideButton && canEditJob && (
        <Button
          size="sm"
          variant="default"
          onClick={() => {
            openModal();
          }}
          icon={<PlusIcon className="size-3.5" strokeWidth={2} />}
          label="New scheduled job"
        />
      )}

      <Modal
        ref={modalRef}
        header={isEdit ? 'Edit Scheduled Job' : 'Create Scheduled Job'}
        subHeader={
          <ScheduledJobTabs
            tabs={TABS}
            activeTabIdx={activeTabIdx}
            onTabChange={setActiveTabIdx}
            tabIdPrefix={tabIdPrefix}
          />
        }
        className="h-[min(700px,calc(100dvh-1rem))] w-[min(900px,calc(100vw-1rem))] max-w-none"
        bodyRef={modalBodyRef}
        open={isOpen}
        footer={
          <Button
            label={isEdit ? 'Save changes' : 'Create job'}
            isLoading={isLoading}
            onClick={handleSubmit}
          />
        }
        onClose={() => {
          const current = new URLSearchParams(
            searchParams ? Array.from(searchParams.entries()) : undefined,
          );

          current.delete('scannerJobId');
          const search = current.toString();
          const query = search ? `?${search}` : '';

          router.push(`${pathname}${query}`);
        }}
      >
        <div
          role="tabpanel"
          id={`${tabIdPrefix}-panel-${activeTabIdx}`}
          aria-labelledby={`${tabIdPrefix}-tab-${activeTabIdx}`}
          className="flex h-full min-h-0 flex-col pb-1"
        >
          {activeTabIdx === 0 ? (
            <JobSettings
              input={input}
              setInput={setInput}
              frequency={frequency}
              setFrequency={setFrequency}
              weekDay={weekDay}
              setWeekDay={setWeekDay}
              monthDay={monthDay}
              setMonthDay={setMonthDay}
              errors={errors}
              setErrors={setErrors}
              isInfluencer={isInfluencer}
            />
          ) : (
            <ActionSettings
              input={input}
              setInput={setInput}
              onRequestDetails={() => {
                setActiveTabIdx(0);
                document.getElementById(`${tabIdPrefix}-tab-0`)?.focus();
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
}
